import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Spacer, TabPanel, Tab, Tabs, useSnackbar } from '@emburse/embark-core';
import { debounce, isEqual } from 'lodash';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { $t, useTranslation } from '../../hooks/useTranslation';
import { Employee } from '../../interfaces/Employee';
import { CurrencySortBy } from '../../interfaces/Money';
import { getCurrenciesSorted, getDepartments } from '../../api/api';
import { getUserRecords, getUserDetail, getEmployeeGLDs, saveUserDetail, hasBillingIncreaseMessage, resendWelcomeEmail, setTemporaryPassword, createTemporaryLoginURL, assumeUserIdentity, getSessionURL, disableManageUsers, getBulkUploadLink, getTreasurerAvailable, sendWelcomeEmails, getApprovalLimits, getSettlementAccount, getUserCount, getApprovalWorkFlowCount, getExpCatMaxOverride, getFilteredUserCount } from '../../api/manageusers';
import { TableAction, defaultManageUsersFilterParameters, ManageUsersUserDetail, ApprovalWorkflowFilter } from '../../interfaces/ManageUsers';
import ManageUsersHeader from './ManageUsersHeader';
import ManageUsersFilters from './ManageUsersFilters';
import ManageUsersTable from './ManageUsersTable';
import ManageUsersUserDrawer from './ManageUsersUserDrawer';
import ManageUsersFiltersDrawer from './ManageUsersFiltersDrawer';
import ManageUsersModal from './ManageUsersModal';
import ManageUsersApprovalWorkflow from './ManageUsersApprovalWorkflow';
import { logger } from '../../util/logger';
import useAccountConfig from '../../hooks/useAccountConfig';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
import { buildUserRoleFilter, resetFiltersToDefault, getFilterCount } from './utils/filterUtils';
import { FilterType } from './utils/types/filterTypes';
function ManageUsers() {
    useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const { accountConfig } = useAccountConfig();
    const filtersDrawerRef = useRef(null);
    const approvalWorkflowRef = useRef(null);
    const [tab, setTab] = useState(0);
    const [currencies, setCurrencies] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);
    const [currenciesLoading, setCurrenciesLoading] = useState(false);
    const [showUserDrawer, setShowUserDrawer] = useState(false);
    const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
    const [userRecords, setUserRecords] = useState([]);
    const [userDetail, setUserDetail] = useState(new ManageUsersUserDetail({}));
    const [loading, setLoading] = useState(true);
    const [filterParameters, setFilterParameters] = useState(defaultManageUsersFilterParameters);
    const [gld1, setGld1] = useState([]);
    const [gld2, setGld2] = useState([]);
    const [gld3, setGld3] = useState([]);
    const [gld4, setGld4] = useState([]);
    const [gld5, setGld5] = useState([]);
    const [hasBillingIncrease, setHasBillingIncrease] = useState(false);
    const [isTreasurerAvailable, setIsTreasurerAvailable] = useState(false);
    const [approvalLimits, setApprovalLimits] = useState([]);
    const [settlementAccounts, setSettlementAccounts] = useState([]);
    const [userCount, setUserCount] = useState(0);
    const [missingApprovers, setMissingApprovers] = useState(0);
    const [missingAccountants, setMissingAccountants] = useState(0);
    const [inactiveApprovers, setInactiveApprovers] = useState(0);
    const [noApprovalLimit, setNoApprovalLimit] = useState(0);
    const [inheritedAccountant, setInheritedAccountant] = useState(0);
    const [sameApprover, setSameApprover] = useState(0);
    const [sameAdditionalApprover, setSameAdditionalApprover] = useState(0);
    const [expCatMaxOverride, setExpCatMaxOverride] = useState([]);
    const [workflowLoading, setWorkflowLoading] = useState(true);
    const [filteredUsersCount, setFilteredUsersCount] = useState(0);
    const [filteredUsersLoading, setFilteredUsersLoading] = useState(false);
    const defaultModalProps = {
        onConfirm: () => { },
        onClose: () => { },
        open: false,
        header: '',
        type: TableAction.ResendWelcomeEmail,
        confirmButtonText: 'Send',
        switchName: ''
    };
    const [modalProps, setModalProps] = useState(defaultModalProps);
    const snackbarHandler = useCallback((message, severity) => {
        enqueueSnackbar(message, { variant: 'alert', AlertProps: { severity } });
    }, [enqueueSnackbar]);
    const catchError = useCallback((err) => {
        logger.log(err);
        setLoading(false);
        removeLoadingState();
        snackbarHandler(err?.Message || err, 'error');
    }, [removeLoadingState, snackbarHandler]);
    const getUsers = useCallback(() => {
        setLoading(true);
        getUserRecords(filterParameters)
            .then((userData) => {
            let parsedData = JSON.parse(JSON.stringify(userData));
            parsedData = parsedData.map((record) => ({ ...record, id: record.UserPK }));
            setUserRecords(parsedData);
        })
            .then(() => setLoading(false))
            .catch(catchError);
    }, [catchError, filterParameters]);
    const fetchFilteredUserCount = useCallback(() => {
        setFilteredUsersLoading(true);
        getFilteredUserCount(filterParameters)
            .then((count) => setFilteredUsersCount(JSON.parse(JSON.stringify(count))))
            .then(() => setFilteredUsersLoading(false))
            .catch(catchError);
    }, [catchError, filterParameters]);
    useEffect(() => {
        fetchFilteredUserCount();
        getUsers();
    }, [fetchFilteredUserCount, getUsers]);
    useEffect(() => {
        hasBillingIncreaseMessage()
            .then((bool) => setHasBillingIncrease(JSON.parse(JSON.stringify(bool))))
            .catch(catchError);
    }, [catchError]);
    useEffect(() => {
        getApprovalLimits()
            .then((data) => setApprovalLimits(JSON.parse(JSON.stringify(data))))
            .catch(catchError);
    }, [catchError]);
    useEffect(() => {
        if (accountConfig?.UIFlags.ACHAccountPartner === 'Convera') {
            getSettlementAccount()
                .then((data) => setSettlementAccounts(JSON.parse(JSON.stringify(data))))
                .catch(catchError);
        }
    }, [accountConfig?.UIFlags.ACHAccountPartner, catchError]);
    useEffect(() => {
        getUserCount()
            .then((count) => setUserCount(JSON.parse(JSON.stringify(count))))
            .catch(catchError);
    }, [catchError]);
    const getApprovalWorkFlowCounts = useCallback(async () => {
        setWorkflowLoading(true);
        try {
            const awaitMissingApprovers = await getApprovalWorkFlowCount(ApprovalWorkflowFilter.MissingApprovers);
            setMissingApprovers(JSON.parse(JSON.stringify(awaitMissingApprovers)));
            const awaitMissingAccountants = await getApprovalWorkFlowCount(ApprovalWorkflowFilter.MissingAccountants);
            setMissingAccountants(JSON.parse(JSON.stringify(awaitMissingAccountants)));
            const awaitInactiveApprovers = await getApprovalWorkFlowCount(ApprovalWorkflowFilter.InactiveApprovers);
            setInactiveApprovers(JSON.parse(JSON.stringify(awaitInactiveApprovers)));
            const awaitNoApprovalLimit = await getApprovalWorkFlowCount(ApprovalWorkflowFilter.NoApprovalLimit);
            setNoApprovalLimit(JSON.parse(JSON.stringify(awaitNoApprovalLimit)));
            const awaitInheritedAccountant = await getApprovalWorkFlowCount(ApprovalWorkflowFilter.InheritedAccountant);
            setInheritedAccountant(JSON.parse(JSON.stringify(awaitInheritedAccountant)));
            const awaitSameApprover = await getApprovalWorkFlowCount(ApprovalWorkflowFilter.SameApprover);
            setSameApprover(JSON.parse(JSON.stringify(awaitSameApprover)));
            const awaitSameAdditionalApprover = await getApprovalWorkFlowCount(ApprovalWorkflowFilter.SameAdditionalApprover);
            setSameAdditionalApprover(JSON.parse(JSON.stringify(awaitSameAdditionalApprover)));
            setWorkflowLoading(false);
        }
        catch (error) {
            setWorkflowLoading(false);
            catchError(error);
        }
    }, [catchError]);
    useEffect(() => {
        getApprovalWorkFlowCounts();
    }, [getApprovalWorkFlowCounts]);
    useEffect(() => {
        const gldMap = {
            1: setGld1,
            2: setGld2,
            3: setGld3,
            4: setGld4,
            5: setGld5
        };
        Object.entries(accountConfig.EmpUDFConfig).forEach(([key, value]) => {
            if (!value.UseSearch) {
                getEmployeeGLDs(parseInt(key, 10))
                    .then((glds) => {
                    const updatedGLDs = Object.entries(glds).map(([pk, val]) => ({
                        pk: parseInt(pk, 10),
                        value: val
                    }));
                    gldMap[key](updatedGLDs);
                })
                    .catch(catchError);
            }
        });
    }, [catchError, accountConfig.EmpUDFConfig]);
    const getAllCurrencies = useCallback(() => {
        setCurrenciesLoading(true);
        getCurrenciesSorted(CurrencySortBy.Name)
            .then(setCurrencies)
            .then(() => setCurrenciesLoading(false))
            .catch(catchError);
    }, [catchError]);
    const getAllDepartments = useCallback(() => {
        setDepartmentsLoading(true);
        getDepartments({ Emp: new Employee() })
            .then(setDepartments)
            .then(() => setDepartmentsLoading(false))
            .catch(catchError);
    }, [catchError]);
    useEffect(() => {
        if (!currenciesLoading && !currencies.length) {
            getAllCurrencies();
        }
        if (!departmentsLoading && !departments.length) {
            getAllDepartments();
        }
    }, [
        currencies.length,
        currenciesLoading,
        departments,
        departmentsLoading,
        getAllCurrencies,
        getAllDepartments
    ]);
    useEffect(() => {
        const filters = localStorage.getItem('manageusersfilters');
        if (window.location.search.includes('FILTER=1') && filters) {
            const parsedFilters = JSON.parse(filters);
            setFilterParameters(parsedFilters);
        }
    }, []);
    const onParameterChange = debounce((param, value) => {
        setFilterParameters((prevState) => ({ ...prevState, [param]: value }));
    }, 750);
    const onDisableManageUsers = () => {
        disableManageUsers()
            .then(() => {
            window.location.href = 'CorpSearchUsers.aspx';
        })
            .catch(catchError);
    };
    const onBulkUpload = () => {
        getBulkUploadLink()
            .then((url) => {
            const parsedURL = JSON.parse(JSON.stringify(url));
            window.open(parsedURL, '_self');
        })
            .catch(catchError);
    };
    const getIsTreasurerAvailable = () => {
        getTreasurerAvailable()
            .then((boolean) => {
            setIsTreasurerAvailable(JSON.parse(JSON.stringify(boolean)));
        })
            .catch(catchError);
    };
    const fetchExpCatMaxOverride = (pk) => {
        if (accountConfig?.Company_UseExpCatMaxOverride) {
            getExpCatMaxOverride(pk)
                .then((data) => {
                const parsedData = JSON.parse(JSON.stringify(data));
                setExpCatMaxOverride(parsedData);
            })
                .catch(catchError);
        }
    };
    useEffect(getIsTreasurerAvailable, [catchError]);
    const onEditUser = (pk) => {
        applyLoadingState();
        getUserDetail(pk)
            .then((userData) => {
            const parsedData = JSON.parse(JSON.stringify(userData));
            setUserDetail(parsedData);
        })
            .then(() => fetchExpCatMaxOverride(pk))
            .then(removeLoadingState)
            .then(() => {
            setShowUserDrawer(true);
        })
            .catch(catchError);
    };
    const onConfirmSendWelcomeEmails = () => {
        const userIDs = userRecords.map((record) => record.UserPK);
        sendWelcomeEmails(userIDs)
            .then((bool) => {
            if (bool) {
                snackbarHandler($t('Welcome emails were successfully sent.'), 'success');
            }
            else {
                snackbarHandler($t('Error, no welcome emails were sent.'), 'warning');
            }
        })
            .catch(catchError);
    };
    const onResendWelcomeEmail = (pk) => {
        resendWelcomeEmail(pk)
            .then(() => snackbarHandler($t('Welcome email was successfully re-sent.'), 'success'))
            .catch(catchError);
    };
    const onTemporaryPassword = (pk, password) => {
        setTemporaryPassword(pk, password)
            .then(() => snackbarHandler($t('The temporary password has been set.'), 'success'))
            .catch(catchError);
    };
    const onCreateTemporaryLoginURL = (pk) => {
        createTemporaryLoginURL(pk)
            .then(() => snackbarHandler($t('A temporary login URL was emailed to this account.'), 'success'))
            .catch(catchError);
    };
    const onMasqueradeAsUser = (pk) => {
        assumeUserIdentity(pk)
            .then((data) => {
            const parsedData = JSON.parse(JSON.stringify(data));
            if (parsedData) {
                const user = userRecords.find((record) => record.UserPK === pk);
                snackbarHandler($t(`You have successfully switched to user ${user.FirstName} ${user.LastName}.`), 'success');
                window.location.href = parsedData;
            }
        })
            .catch(catchError);
    };
    const onModifyRecords = () => {
        const filters = JSON.stringify(filterParameters);
        localStorage.setItem('manageusersfilters', filters);
        const userIDs = userRecords.map((record) => record.UserPK);
        getSessionURL(userIDs)
            .then((url) => {
            const parsedURL = JSON.parse(JSON.stringify(url));
            window.open(parsedURL, '_self');
        })
            .catch(catchError);
    };
    const onApproverFor = (pk) => {
        const user = userRecords.find((record) => record.UserPK === pk);
        if (!user)
            return;
        const filterUser = {
            UserEmail: user.UserEmail,
            EmpPK: user.EmpPK
        };
        setFilterParameters((prevState) => buildUserRoleFilter(FilterType.Approver, prevState, filterUser));
    };
    const onAccountantFor = (pk) => {
        const user = userRecords.find((record) => record.UserPK === pk);
        if (!user)
            return;
        const filterUser = {
            UserEmail: user.UserEmail,
            EmpPK: user.EmpPK
        };
        setFilterParameters((prevState) => buildUserRoleFilter(FilterType.Accountant, prevState, filterUser));
    };
    const onModalClose = () => setModalProps(defaultModalProps);
    const getFilterCountWrapper = () => {
        return getFilterCount(filterParameters);
    };
    const onResetFilters = () => {
        setFilterParameters(resetFiltersToDefault());
        if (filtersDrawerRef.current) {
            filtersDrawerRef.current.resetFilters();
        }
    };
    const onSendWelcomeEmails = () => {
        setModalProps({
            onConfirm: onConfirmSendWelcomeEmails,
            onClose: onModalClose,
            open: true,
            header: 'Send Welcome Emails',
            type: TableAction.SendWelcomeEmails,
            confirmButtonText: 'Send',
            switchName: ''
        });
    };
    const onTabChange = (_event, newTabIndex) => {
        setTab(newTabIndex);
    };
    const onTakeAction = (pk, action) => {
        const user = userRecords.find((record) => record.UserPK === pk);
        switch (action) {
            case TableAction.EditUser:
                onEditUser(pk);
                break;
            case TableAction.ResendWelcomeEmail:
                setModalProps({
                    onConfirm: () => onResendWelcomeEmail(pk),
                    onClose: onModalClose,
                    open: true,
                    header: "Resend User's Welcome Email",
                    type: TableAction.ResendWelcomeEmail,
                    confirmButtonText: 'Send',
                    switchName: ''
                });
                break;
            case TableAction.TemporaryPassword:
                setModalProps({
                    onConfirm: (password) => onTemporaryPassword(pk, password),
                    onClose: onModalClose,
                    open: true,
                    header: 'Create Temporary Password',
                    type: TableAction.TemporaryPassword,
                    confirmButtonText: 'Save',
                    switchName: ''
                });
                break;
            case TableAction.CreateTemporaryLoginURL:
                setModalProps({
                    onConfirm: () => onCreateTemporaryLoginURL(pk),
                    onClose: onModalClose,
                    open: true,
                    header: 'Temporary Login URL',
                    type: TableAction.CreateTemporaryLoginURL,
                    confirmButtonText: 'Send',
                    switchName: ''
                });
                break;
            case TableAction.MasqueradeAsUser:
                setModalProps({
                    onConfirm: () => onMasqueradeAsUser(pk),
                    onClose: onModalClose,
                    open: true,
                    header: 'Switch To User',
                    type: TableAction.MasqueradeAsUser,
                    confirmButtonText: 'Switch',
                    switchName: `${user.FirstName} ${user.LastName}`
                });
                break;
            case TableAction.ApproverFor:
                onApproverFor(pk);
                break;
            case TableAction.AccountantFor:
                onAccountantFor(pk);
                break;
            default:
                break;
        }
    };
    const saveDetail = (data, location) => {
        let hasErrors = false;
        applyLoadingState();
        setUserDetail(new ManageUsersUserDetail(data));
        saveUserDetail(data)
            .then((returnData) => {
            if (location) {
                location();
                setShowUserDrawer(false);
                removeLoadingState();
                return;
            }
            const parsedData = JSON.parse(JSON.stringify(returnData));
            if (parsedData.Success) {
                setShowUserDrawer(false);
                setUserDetail(new ManageUsersUserDetail({}));
                if (!data.UserPK) {
                    if (data.DoNotSendWelcomeEmail) {
                        snackbarHandler($t('New user added. Open the user details to send a welcome email when ready.'), 'success');
                    }
                    else {
                        snackbarHandler($t('New User Added. A welcome email has been sent to the user.'), 'success');
                    }
                }
                else {
                    snackbarHandler($t('Your changes have been saved.'), 'success');
                }
            }
            else {
                hasErrors = true;
                parsedData.ValidationMessages.forEach((message) => {
                    snackbarHandler(message, 'error');
                });
            }
            removeLoadingState();
        })
            .then(() => {
            if (!hasErrors) {
                getUsers();
                getApprovalWorkFlowCounts();
                if (approvalWorkflowRef.current) {
                    approvalWorkflowRef.current.loadUserRecords();
                }
            }
        })
            .catch(catchError);
    };
    const workflowCount = missingApprovers + missingAccountants + inactiveApprovers;
    return (_jsxs(SingleColumnLayout, { children: [_jsx(ManageUsersModal, { ...modalProps }), _jsx(ManageUsersHeader, { onModifyRecords: onModifyRecords, onNewUser: () => setShowUserDrawer(true), onDisableManageUsers: onDisableManageUsers, onBulkUpload: onBulkUpload, loading: loading, accountConfig: accountConfig, tableCount: userRecords.length }), _jsx(Spacer, { size: 20 }), userCount > 200 && (_jsxs(_Fragment, { children: [_jsx(Alert, { severity: "warning", style: { width: 'fit-content' }, children: $t('Note that the page shows 200 users at a time. To view or edit a user not shown, search or filter accordingly.') }), _jsx(Spacer, { size: 30 })] })), _jsx(Spacer, { size: 10 }), _jsxs(Tabs, { id: "ManageUsersTab", onChange: onTabChange, children: [_jsx(Tab, { label: $t('User Records'), size: "medium", style: { opacity: 1 } }), _jsx(Tab, { label: $t('Approval Workflow'), icon: workflowCount > 0 && (_jsx(Badge, { badgeContent: workflowCount, color: "primary", style: { marginLeft: '20px' } })), iconPosition: "end", size: "medium", style: { opacity: workflowLoading ? 0.5 : 1 } })] }), _jsxs(TabPanel, { value: tab, index: 0, children: [_jsx(Spacer, { size: 35 }), _jsx(ManageUsersFilters, { filterParameters: filterParameters, onParameterChange: onParameterChange, onAdvancedFilters: () => setShowFiltersDrawer(true), loading: loading, hasChanges: !isEqual(filterParameters, defaultManageUsersFilterParameters), onReset: onResetFilters, getFilterCount: getFilterCountWrapper, filteredUsersCount: filteredUsersCount, availableUsersCount: userRecords.length, filteredUsersLoading: filteredUsersLoading }), _jsx(Spacer, { size: 25 }), _jsx(ManageUsersTable, { data: userRecords, onTakeAction: onTakeAction, onNewUser: () => setShowUserDrawer(true), loading: loading, accountConfig: accountConfig, filterParameters: filterParameters, hasChanges: !isEqual(filterParameters, defaultManageUsersFilterParameters), onSendWelcomeEmails: onSendWelcomeEmails })] }), _jsxs(TabPanel, { value: tab, index: 1, children: [_jsx(Spacer, { size: 35 }), _jsx(ManageUsersApprovalWorkflow, { onEditUser: onEditUser, missingApprovers: missingApprovers, missingAccountants: missingAccountants, inactiveApprovers: inactiveApprovers, noApprovalLimit: noApprovalLimit, inheritedAccountant: inheritedAccountant, sameApprover: sameApprover, sameAdditionalApprover: sameAdditionalApprover, ref: approvalWorkflowRef, workflowLoading: workflowLoading })] }), _jsx(ManageUsersUserDrawer, { open: showUserDrawer, currencies: currencies, userData: new ManageUsersUserDetail(userDetail), departments: departments, gld1: gld1, gld2: gld2, gld3: gld3, gld4: gld4, gld5: gld5, approvalLimits: approvalLimits, hasBillingIncrease: hasBillingIncrease, onCancel: () => {
                    setShowUserDrawer(false);
                    setUserDetail(new ManageUsersUserDetail({}));
                }, onSave: saveDetail, onApproverFor: onApproverFor, onAccountantFor: onAccountantFor, closeDrawer: () => setShowUserDrawer(false), accountConfig: accountConfig, isTreasurerAvailable: isTreasurerAvailable, settlementAccounts: settlementAccounts, expCatMaxOverride: expCatMaxOverride }), _jsx(ManageUsersFiltersDrawer, { open: showFiltersDrawer, departments: departments, filterParameters: filterParameters, accountConfig: accountConfig, gld1: gld1, gld2: gld2, gld3: gld3, gld4: gld4, gld5: gld5, approvalLimits: approvalLimits, isTreasurerAvailable: isTreasurerAvailable, openUpdated: () => setShowFiltersDrawer(false), onCancel: () => setShowFiltersDrawer(false), onSave: setFilterParameters, ref: (element) => {
                    filtersDrawerRef.current = element;
                } })] }));
}
export default ManageUsers;
